import type { Vector3 } from "three";
import type { SugarModel } from "../../loader/SugarModel";
import {
  DragConstraintPipeline,
  type DragSessionPort,
} from "./DragSessionPort";
import { createConstrainedDragSession } from "./ProductSceneDrag";

export type ProductSceneMoverOptions = {
  /** Skip pipeline for this model (e.g. whole configurator root). */
  bypass?: (model: SugarModel) => boolean;
  onBypassBegin?: () => void;
  onBypassCancel?: () => void;
};

/**
 * Shared scene-product grab → move → drop/cancel.
 *
 * Products own:
 * - which {@link DragSessionConstraint}s go in {@link DragConstraintPipeline}
 * - how they compute the candidate world position (ground plane, wall surface, …)
 *
 * Reuse from any viewport element / controller:
 * ```ts
 * const mover = new ProductSceneMover(pipeline, { bypass });
 * mover.grab(model);
 * mover.moveTo(candidate);
 * mover.drop(); // or mover.cancel()
 * ```
 */
export class ProductSceneMover {
  private _model?: SugarModel;
  private _session?: DragSessionPort<void, Vector3, void>;
  private _active = false;

  constructor(
    readonly pipeline: DragConstraintPipeline,
    private readonly options: ProductSceneMoverOptions = {},
  ) {}

  get active(): boolean {
    return this._active;
  }

  get model(): SugarModel | undefined {
    return this._model;
  }

  grab(model: SugarModel): void {
    if (this._active) this.cancel();
    this._model = model;
    this._session = createConstrainedDragSession(
      model,
      this.pipeline,
      this.options,
    );
    this._session.begin();
    this._active = true;
  }

  /** Resolve candidate through the pipeline and write `model.position`. */
  moveTo(candidate: Vector3): Vector3 {
    if (!this._active || !this._model || !this._session) return candidate;
    this._session.move(candidate);
    return this._model.position;
  }

  /**
   * Resolve through the pipeline without requiring an active session write.
   * Use for multi-pass (e.g. wall refresh then re-resolve).
   */
  resolve(candidate: Vector3): Vector3 {
    if (!this._model) return candidate;
    return this.resolveFor(this._model, candidate);
  }

  /** Resolve for an arbitrary model (hover / preview before grab). */
  resolveFor(model: SugarModel, candidate: Vector3): Vector3 {
    if (this.options.bypass?.(model)) return candidate.clone();
    return this.pipeline.resolve(model, candidate);
  }

  drop(): void {
    if (!this._active) return;
    this._session?.commit();
    this._clear();
  }

  cancel(): void {
    if (!this._active) return;
    this._session?.cancel();
    this._clear();
  }

  private _clear(): void {
    this._session = undefined;
    this._model = undefined;
    this._active = false;
  }
}
